import { useState, useEffect, useRef, useCallback } from 'react';
import { useChatPanel } from '../contexts/ChatPanelContext';
import { forgetOpenPending, sendMessageStreaming, submitChatFeedback } from '../services/chatService';
import { analytics } from '../utils/analytics';
import { useContent } from '../utils/content';
import { MessageActionHandler } from '../utils/messageActionHandler';
import CONVERSAYION_MESSAGE_TYPES from '../constants/conversationMessageTypes';
import { applyAnswerDelta, applyAiMessage, isConfirmationAction, localKey } from '../utils/chatStreamReducer';

// The longest message the backend takes (schemas.CHAT_QUERY_MAX_CHARS); a longer one gets a 422.
// The chat input stops there (components/ChatInterface.js).
export const CHAT_QUERY_MAX_CHARS = 20000;

/**
 * IMPROVED: Single source of truth chat hook
 * Uses only ChatPanelContext for conversation state management
 *
 * A message goes out with the page it is about (ChatPanelContext's getPage), so KeyTa knows
 * which property "this" means; the chat is one conversation.
 */
const useChat = (options = {}) => {
  const {
    onActionExecute = null,
    restoreFromPanel = false,
    initialQuery = '',
    enableAutoSend = false
  } = options;

  const {
    chatPanelState,
    updateConversation,
    navigateWithChat,
    setAiThinking,
    setLoading,
    setVisitorTokenLimitReached,
    getPage,
    getChatEpoch,
    getDraft,
    setDraft,
  } = useChatPanel();

  // Only UI state - conversation comes directly from context. The input starts from the
  // context's draft: the panel mounts again on every page, and typed text must not be lost.
  const [query, setQuery] = useState(() => initialQuery || getDraft());
  const [feedbackSubmittingId, setFeedbackSubmittingId] = useState(null);
  // tokenLimitReached is canonical in ChatPanelContext so it persists across route changes
  const tokenLimitReached = chatPanelState.visitorTokenLimitReached || false;
  // What a message over CHAT_QUERY_MAX_CHARS gets instead of "try again" (null until the copy loads).
  const tooLongText = useContent('agent-approvals')?.chat?.send?.tooLong || null;

  // Refs for lifecycle management
  const isInitialQueryRef = useRef(!!initialQuery);
  const isMounted = useRef(false);
  const messagesEndRef = useRef(null);
  const autoSendTriggeredRef = useRef(false);
  const latestThreadIdRef = useRef(null);
  const loadingRef = useRef(chatPanelState.loading);

  // Use conversation directly from context (single source of truth)
  const conversation = chatPanelState.conversation || [];
  const loading = chatPanelState.loading;
  const threadId = chatPanelState.threadId;

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // The draft follows the input, so the next page's panel opens with it.
  useEffect(() => {
    setDraft(query);
  }, [query, setDraft]);

  useEffect(() => {
    if (threadId) {
      latestThreadIdRef.current = threadId;
    }
  }, [threadId]);

  // Load chat history on mount - REMOVED to prevent duplicate calls
  // ChatPanelContext handles initialization globally
  useEffect(() => {
    // Prevent double call in strict mode
    if (isMounted.current) return;
    isMounted.current = true;
  }, []);



  // Auto-scroll to bottom when conversation updates
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation.length]); // Use conversation.length to trigger on new messages

  // Removed: Sync local conversation with panel state (no longer needed)

  // Handle chat actions (navigation, etc.)
  const handleChatAction = useCallback((action) => {
    if (!action) return;

    switch (action.type?.toUpperCase()) {
      case 'NAVIGATE':
        // Use navigateWithChat to maintain chat context and show chat panel
        navigateWithChat(action.target, action.params);
        break;
      
      case 'REDIRECT':
        // Direct navigation for redirects
        const url = action.params ? `${action.target}?${action.params}` : action.target;
        window.location.href = url;
        break;
      
      case 'REQUEST_CONFIRMATION':
        // Nothing to do here: the message renders a confirm card (components/chat/ConfirmActionCard),
        // and the action runs only when the user presses Confirm on it.
        break;

      // Add more action types as needed
      default:
        console.warn('Unknown action type:', action.type);
    }
  }, [navigateWithChat]);

  // Send message function - updates context directly
  const handleSendMessage = useCallback(async (e, isInitial = false, messageOverride = null, talkMode = false) => {
    console.log('🎤 useChat.handleSendMessage called with:', { e: !!e, isInitial, messageOverride: !!messageOverride, talkMode });
    
    e?.preventDefault();
    
    // Use messageOverride if provided, otherwise use query state
    const messageToSend = (messageOverride ?? query ?? '').trim();
    if (!messageToSend || loadingRef.current) {
      return false;
    }

    setLoading(true);
    setAiThinking(true); // Start AI thinking animation
    analytics.chatMessage();
    const userMessage = messageToSend;
    
    // Only clear input if we're using the query state (not override)
    if (!messageOverride) {
      setQuery(''); // Clear input immediately
    }

    // Add user message to context conversation. The client time is a placeholder
    // until the backend echoes the message back with its stored created_at.
    updateConversation(prev => [...prev,
      {
        type: 'user',
        text: userMessage,
        message_type: '',
        created_at: new Date().toISOString(),
        clientKey: localKey('user'),
      },
    ]);

    // The page this message is about.
    const page = typeof getPage === 'function' ? getPage() : null;
    // The account's chat this message was sent in: when the account changes while the answer is
    // still coming, the rest of it would land in the next account's chat, so it stops there.
    const epoch = typeof getChatEpoch === 'function' ? getChatEpoch() : null;
    const sameChat = () => epoch === null || getChatEpoch() === epoch;

    try {
      // Handle actions and update conversation
      let lastAction = null;
      let hasReceivedFirstChunk = false;

      for await (const chunk of sendMessageStreaming(userMessage, 'user', null, talkMode, { page })) {
        if (!sameChat()) {
          break;
        }
        if (!chunk?.message) {
          continue;
        }
        const message_type = chunk?.message_type || 'general';

        if (message_type === CONVERSAYION_MESSAGE_TYPES.HUMAN) {
          if (chunk.created_at || chunk.id || chunk.thread_id) {
            // Backend echo of the user message: the server timestamp, id and thread.
            updateConversation(prev => {
              const lastUserIdx = prev.map(msg => msg.type).lastIndexOf('user');
              if (lastUserIdx === -1) return prev;
              const updatedConversation = [...prev];
              updatedConversation[lastUserIdx] = {
                ...updatedConversation[lastUserIdx],
                ...(chunk.created_at ? { created_at: chunk.created_at } : {}),
                ...(chunk.id ? { id: chunk.id } : {}),
                ...(chunk.thread_id ? { thread_id: chunk.thread_id } : {}),
              };
              return updatedConversation;
            });
          }
          continue;
        }

        // Stop AI thinking animation on first AI response chunk
        if (!hasReceivedFirstChunk) {
          setAiThinking(false);
          hasReceivedFirstChunk = true;
        }

        if (chunk.thread_id) {
          latestThreadIdRef.current = chunk.thread_id;
        }

        if (message_type === CONVERSAYION_MESSAGE_TYPES.AI_DELTA) {
          // One token of the answer; the final AI line replaces the streaming bubble.
          updateConversation(prev => applyAnswerDelta(prev, chunk));
          continue;
        }

        // Track the last action
        if (chunk.action) {
          lastAction = chunk.action;
          // A new confirm card: what the cards know about open confirmations is now stale.
          if (isConfirmationAction(chunk.action)) forgetOpenPending();
        }

        updateConversation(prev => {
          const lastMessage = prev[prev.length - 1];
          const incomingVisualizations = Array.isArray(chunk.visualizations)
            ? chunk.visualizations
            : null;

          // Use the utility to process message actions
          const formattedText = MessageActionHandler.processMessageAction(
            chunk.message,
            chunk.action
          );

          // Create message object with audio_data if present
          const messageObject = {
            type: 'ai',
            text: formattedText,
            message_type,
            action: lastAction
          };

          if (incomingVisualizations && incomingVisualizations.length > 0) {
            messageObject.visualizations = incomingVisualizations;
          } else if (Array.isArray(lastMessage?.visualizations) && lastMessage.visualizations.length > 0) {
            messageObject.visualizations = lastMessage.visualizations;
          }

          // Cards for what the app made (show_artifact), on the final answer only.
          if (Array.isArray(chunk.artifacts) && chunk.artifacts.length > 0) {
            messageObject.artifacts = chunk.artifacts;
          }

          if (chunk.id) {
            messageObject.id = chunk.id;
          }

          if (chunk.thread_id) {
            messageObject.thread_id = chunk.thread_id;
          }

          // Include audio_data if present (from talk_mode)
          if (chunk.audio_data) {
            messageObject.audio_data = chunk.audio_data;
            messageObject.audio_format = chunk.audio_format || 'mp3';
            console.log('🎤 Received audio data in talk mode:', {
              hasAudio: !!chunk.audio_data,
              format: chunk.audio_format,
              audioLength: chunk.audio_data ? chunk.audio_data.length : 0
            });
          }

          return applyAiMessage(prev, messageObject);
        });
      }

      // Execute action if present. A confirm card is not a page action: the message shows it.
      if (lastAction && sameChat()) {
        if (isConfirmationAction(lastAction)) {
          handleChatAction(lastAction);
        } else if (onActionExecute) {
          onActionExecute(lastAction);
        } else {
          handleChatAction(lastAction);
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      if (!sameChat()) {
        // Another account's chat is on screen now: nothing of this message goes into it.
        return true;
      }
      setAiThinking(false); // Clear AI thinking on error
      if (error.status === 402 || error.message?.includes('402')) {
        setVisitorTokenLimitReached(true);
        // Remove the user message that was optimistically added
        updateConversation(prev => prev.filter((_, i) => i !== prev.length - 1 || prev[i].text !== userMessage));
      } else {
        // A message over the length KeyTa takes (422) never goes through as it is: say so,
        // rather than "try again".
        const tooLong = error.status === 422 && tooLongText;
        updateConversation(prev => [...prev, {
          type: 'ai',
          text: tooLong
            ? tooLongText.replace('{max}', CHAT_QUERY_MAX_CHARS.toLocaleString('en-CA'))
            : 'An error occurred while getting the response. Please try again!',
          message_type: CONVERSAYION_MESSAGE_TYPES.ERROR
        }]);
      }
    } finally {
      // After an account change the next account's chat load owns these.
      if (sameChat()) {
        setLoading(false);
        setAiThinking(false); // Ensure AI thinking is cleared
      }
    }

    return true;
  }, [query, getPage, getChatEpoch, onActionExecute, handleChatAction, updateConversation, setAiThinking, setLoading, setVisitorTokenLimitReached, tooLongText]);

  const handleMessageFeedback = useCallback(async (message, feedbackValue) => {
    // The thread the message was stored in, else the chat's.
    const resolvedThreadId =
      message?.thread_id ||
      threadId ||
      latestThreadIdRef.current ||
      [...conversation].reverse().find((msg) => msg?.thread_id)?.thread_id ||
      null;

    console.log('🎤 handleMessageFeedback called with:', {
      messageId: message?.id,
      threadId,
      messageThreadId: message?.thread_id,
      resolvedThreadId,
    });

    if (!message?.id || !resolvedThreadId) {
      console.warn('Missing message id or thread id for feedback submission', {
        messageId: message?.id,
        resolvedThreadId,
      });
      return;
    }

    const nextFeedbackValue = message?.feedback_value === feedbackValue ? null : feedbackValue;

    try {
      setFeedbackSubmittingId(message.id);

      const result = await submitChatFeedback({
        thread_id: resolvedThreadId,
        message_id: message.id,
        feedback_value: nextFeedbackValue,
      });

      const savedFeedback = Object.prototype.hasOwnProperty.call(result || {}, 'feedback_value')
        ? result.feedback_value
        : nextFeedbackValue;
      updateConversation(prev => prev.map((msg) => {
        if (msg?.id === message.id) {
          return {
            ...msg,
            feedback_value: savedFeedback,
          };
        }
        return msg;
      }));
    } catch (error) {
      console.error('Failed to submit chat feedback:', error);
    } finally {
      setFeedbackSubmittingId(null);
    }
  }, [threadId, conversation, updateConversation]);

  // Handle initial query auto-send - must be after handleSendMessage definition
  useEffect(() => {
    const preparedInitialQuery = (initialQuery || '').trim();

    if (!enableAutoSend ||
        !isInitialQueryRef.current ||
        !preparedInitialQuery ||
        loading ||
        autoSendTriggeredRef.current) {
      return;
    }

    // Delay keeps StrictMode from double-sending during development remount checks.
    const timer = setTimeout(async () => {
      const didSend = await handleSendMessage(null, true, preparedInitialQuery);

      if (!didSend) {
        return;
      }

      autoSendTriggeredRef.current = true;
      isInitialQueryRef.current = false;
      // Sent, so it is no longer a draft (else it would follow the user into the panel).
      setQuery((current) => (current.trim() === preparedInitialQuery ? '' : current));

      // Clear one-time initial query markers after successful send.
      const historyState = window.history.state || {};
      const userState = historyState.usr || {};
      const nextUserState = { ...userState };
      const hadStateInitialQuery = Object.prototype.hasOwnProperty.call(userState, 'initialQuery');

      if (hadStateInitialQuery) {
        delete nextUserState.initialQuery;
      }

      const currentUrl = new URL(window.location.href);
      const hadQueryInitialQuery = currentUrl.searchParams.has('q');

      if (hadQueryInitialQuery) {
        currentUrl.searchParams.delete('q');
      }

      const nextSearch = currentUrl.searchParams.toString();
      const nextUrl = `${currentUrl.pathname}${nextSearch ? `?${nextSearch}` : ''}${currentUrl.hash}`;

      if (hadStateInitialQuery || hadQueryInitialQuery) {
        window.history.replaceState(
          { ...historyState, usr: nextUserState },
          document.title,
          nextUrl
        );
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [enableAutoSend, initialQuery, loading, handleSendMessage]);

  return {
    // State (conversation comes directly from context)
    query,
    setQuery,
    conversation, // Direct reference to context conversation
    loading,
    tokenLimitReached,
    
    // Refs
    messagesEndRef,
    
    // Functions
    handleSendMessage,
    handleChatAction,
    handleMessageFeedback,
    feedbackSubmittingId,
  };
};

export default useChat;
